// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/learning/LearningInsightPanel.jsx

import React, { useMemo } from "react";
import PreviousResolutionCard from "./PreviousResolutionCard";
import PatternRecognitionCard from "./PatternRecognitionCard";
import RecommendationQualityCard from "./RecommendationQualityCard";
import OutcomeHistoryCard from "./OutcomeHistoryCard";
import ConfidenceTrendCard from "./ConfidenceTrendCard";

const guardrailsAreSafe = (guardrails = {}) => (
  guardrails.autonomous_decision_making === false
  && guardrails.priority_mutation === false
  && guardrails.recommendation_mutation === false
  && guardrails.workflow_execution === false
  && guardrails.approval_execution === false
  && guardrails.sop_rewrite === false
  && guardrails.cloud_resource_mutation === false
);

const GUARDRAIL_LABELS = {
  autonomous_decision_making: "Autonomous decisions",
  priority_mutation: "Priority changes",
  recommendation_mutation: "Recommendation changes",
  workflow_execution: "Workflow execution",
  approval_execution: "Approval execution",
  sop_rewrite: "SOP rewrites",
  cloud_resource_mutation: "Cloud mutations",
};

const EXPECTED_GUARDRAIL_ORDER = [
  "autonomous_decision_making",
  "priority_mutation",
  "recommendation_mutation",
  "workflow_execution",
  "approval_execution",
  "sop_rewrite",
  "cloud_resource_mutation",
];

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function taskTitle(task = {}, context = {}) {
  return (
    clean(context?.task?.title)
    || clean(task?.title)
    || clean(task?.summary)
    || clean(task?.rule_id)
    || "the selected task"
  );
}

function guardrailDisplayRows(guardrails = {}) {
  const knownRows = EXPECTED_GUARDRAIL_ORDER.map((key) => {
    const value = guardrails?.[key];
    return {
      key,
      label: GUARDRAIL_LABELS[key] || key.replace(/_/g, " "),
      enabled: value === true,
      value,
    };
  });

  const extraRows = Object.entries(guardrails || {})
    .filter(([key]) => !EXPECTED_GUARDRAIL_ORDER.includes(key))
    .map(([key, value]) => ({
      key,
      label: GUARDRAIL_LABELS[key] || key.replace(/_/g, " "),
      enabled: value === true,
      value,
    }));

  return [...knownRows, ...extraRows];
}

function guardrailPillClass(safeGuardrails) {
  return safeGuardrails
    ? "learning-guardrail-pill learning-guardrail-pill-safe"
    : "learning-guardrail-pill learning-guardrail-pill-review";
}

function AdvisoryNotice({ guardrails }) {
  const safeGuardrails = guardrailsAreSafe(guardrails || {});
  const rows = guardrailDisplayRows(guardrails || {});

  return (
    <section className="learning-card learning-guardrails-card">
      <div className="learning-card-header">
        <div>
          <p className="learning-eyebrow">Guardrails</p>
          <h4>Advisory-Only Learning</h4>
        </div>
        <span className={guardrailPillClass(safeGuardrails)}>
          {safeGuardrails ? "Guardrails locked" : "Review guardrails"}
        </span>
      </div>

      <p className="learning-muted-copy">
        Learning can inform confidence, guidance, and future planning. It does not execute workflows,
        approve changes, rewrite SOPs, mutate priorities, or change cloud resources.
      </p>

      <div className="learning-chip-row" aria-label="Learning guardrails">
        {rows.map((row) => (
          <span
            className={row.enabled ? "learning-chip learning-chip-warning" : "learning-chip learning-chip-safe"}
            key={row.key}
            title={`${row.label}: ${row.enabled ? "On — review required" : "Off — locked"}`}
          >
            {row.label}: {row.enabled ? "On · Review" : "Off"}
          </span>
        ))}
      </div>
    </section>
  );
}

export default function LearningInsightPanel({
  task = {},
  context = null,
  loading = false,
  error = "",
  refreshState = "idle",
  onRefresh,
}) {
  const learning = context && typeof context === "object" ? context : null;

  const patterns = useMemo(() => {
    if (!learning) return [];
    return Array.isArray(learning.patterns) && learning.patterns.length
      ? learning.patterns
      : Array.isArray(learning.global_patterns)
        ? learning.global_patterns
        : [];
  }, [learning]);

  const connectorPatterns = learning?.connector_patterns || {};
  const frameworkPatterns = learning?.framework_patterns || {};
  const commonBlockers = learning?.common_blockers || learning?.summary?.common_blockers || [];
  const safeGuardrails = guardrailsAreSafe(learning?.guardrails || {});
  const effectiveRefreshState = refreshState || (loading && learning ? "refreshing" : "idle");
  const isRefreshing = effectiveRefreshState === "refreshing";
  const isRefreshSuccess = effectiveRefreshState === "success";
  const isRefreshError = effectiveRefreshState === "error";
  const refreshButtonLabel = isRefreshing
    ? "Refreshing…"
    : isRefreshSuccess
      ? "Refreshed"
      : isRefreshError
        ? "Retry Refresh"
        : "Refresh Learning";
  const refreshMessage = isRefreshError
    ? "Learning refresh failed. Existing context is still shown."
    : "";

  if (loading && !learning) {
    return (
      <div className="learning-insight-panel learning-insight-panel-loading">
        <div className="learning-panel-header">
          <div>
            <p className="learning-eyebrow">AI Learning · Continuous Improvement</p>
            <h3>Learning Insights</h3>
            <p>Loading prior outcomes, similar issue recall, effectiveness, and confidence evolution…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="learning-insight-panel learning-insight-panel-error">
        <div className="learning-panel-header">
          <div>
            <p className="learning-eyebrow">AI Learning · Continuous Improvement</p>
            <h3>Learning Insights</h3>
            <p>{error}</p>
          </div>
          {onRefresh ? (
            <button type="button" className="learning-refresh-button" onClick={onRefresh} disabled={loading}>
              {loading ? "Refreshing…" : "Retry"}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!learning) {
    return (
      <div className="learning-insight-panel learning-insight-panel-empty">
        <div className="learning-panel-header">
          <div>
            <p className="learning-eyebrow">AI Learning · Continuous Improvement</p>
            <h3>Learning Insights</h3>
            <p>Select a task to view organizational memory, previous outcomes, and advisory learning signals.</p>
          </div>
          {onRefresh ? (
            <button type="button" className="learning-refresh-button" onClick={onRefresh} disabled={isRefreshing}>
              {refreshButtonLabel}
            </button>
          ) : null}
          {refreshMessage ? (
            <span className={`learning-refresh-inline learning-refresh-inline-${effectiveRefreshState}`} role="status">
              {refreshMessage}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`learning-insight-panel${isRefreshing ? " learning-insight-panel-refreshing" : ""}`} aria-busy={isRefreshing}>
      <div className="learning-panel-header">
        <div>
          <p className="learning-eyebrow">AI Learning · Continuous Improvement</p>
          <h3>Learning Insights</h3>
          <p>
            OASYS remembers what worked for {taskTitle(task, learning)} and uses that history to explain
            confidence, similar work, execution quality, and validation readiness. Learning remains advisory only.
          </p>
        </div>
        <div className="learning-header-actions">
          <span className={guardrailPillClass(safeGuardrails)}>
            {safeGuardrails ? "Advisory only" : "Review guardrails"}
          </span>
          {onRefresh ? (
            <button type="button" className="learning-refresh-button" onClick={onRefresh} disabled={isRefreshing}>
              {refreshButtonLabel}
            </button>
          ) : null}
          {refreshMessage ? (
            <span className={`learning-refresh-inline learning-refresh-inline-${effectiveRefreshState}`} role="status">
              {refreshMessage}
            </span>
          ) : null}
        </div>
      </div>

      <div className="learning-panel-grid">
        <PreviousResolutionCard
          recall={learning?.similar_issue_recall}
          outcomes={learning?.previous_outcomes}
        />
        <RecommendationQualityCard
          recommendation={learning?.recommendation_effectiveness || learning?.effectiveness?.recommendation_quality}
          execution={learning?.execution_plan_effectiveness || learning?.effectiveness?.execution_plan_quality}
          validation={learning?.validation_learning}
        />
        <ConfidenceTrendCard confidence={learning?.confidence_adjustment} />
        <OutcomeHistoryCard previousOutcomes={learning?.previous_outcomes} />
      </div>

      <PatternRecognitionCard
        patterns={patterns}
        connectorPatterns={connectorPatterns}
        frameworkPatterns={frameworkPatterns}
        commonBlockers={commonBlockers}
      />

      <AdvisoryNotice guardrails={learning?.guardrails || {}} />
    </div>
  );
}
